'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { fetchManufacturers } from '@eliadmin/core/src/api/manufacturers/manufacturers-api';
import { BrandCard } from './brand-card';

export const BrandsList = ({ locale }: { locale: string }) => {
  const brandsQuery = useInfiniteQuery({
    queryKey: [CacheKey.MANUFACTURERS, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchManufacturers({
        locale,
        query: {
          page: pageParam,
          results: 10,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page <= lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const brands = brandsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  const { t } = useTranslations();

  return (
    <>
      <div
        className={cn(
          'custom-container',
          'grid grid-cols-2 gap-x-2 gap-y-8 sm:grid-cols-2 sm:gap-x-6 md:grid-cols-3 md:gap-y-10 lg:grid-cols-4 xl:grid-cols-5 xl:gap-x-8',
        )}
      >
        {brandsQuery.isLoading && <BrandsSkeleton amount={10} />}
        {brandsQuery.isSuccess && brands.map(brand => <BrandCard brand={brand} key={brand.slug} />)}
      </div>

      {brandsQuery.hasNextPage && (
        <Button
          onClick={() => brandsQuery.fetchNextPage()}
          isLoading={brandsQuery.isFetchingNextPage}
          className={cn('mt-16', { hidden: !brandsQuery.hasNextPage })}
          variant={'outline'}
        >
          {t('text338')}
        </Button>
      )}
    </>
  );
};

const BrandsSkeleton = ({ amount }: { amount: number }) => {
  return (
    <div
      className={cn(
        'animate-pulse',
        'col-span-1 grid w-full grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-3',
      )}
    >
      {Array.from({ length: amount }).map((_, index) => (
        <div key={index} className="col-span-1 aspect-square w-full bg-neutral-100" />
      ))}
    </div>
  );
};
